/**
 * The organisation's currency. The ledger is kept in US dollars (model providers price in them);
 * the organisation picks the currency its people see, and the platform converts at a rate the
 * European Central Bank publishes daily or at one an administrator sets by hand.
 *
 * Mirrored by the workbench (shared/schemas/money.ts), which shows costs in the same currency.
 */
import { z } from "zod";
/** The currencies the daily rate feed covers, plus the US dollar itself. */
export const CURRENCIES = ["USD", "AUD", "BGN", "BRL", "CAD", "CHF", "CNY", "CZK", "DKK", "EUR", "GBP", "HKD", "HUF", "IDR", "ILS", "INR", "ISK", "JPY", "KRW", "MXN", "MYR", "NOK", "NZD", "PHP", "PLN", "RON", "SEK", "SGD", "THB", "TRY", "ZAR"];
export const currencyCodeSchema = z.enum(CURRENCIES);
/** Short names for the picker; Intl has the symbols. */
export const CURRENCY_NAMES = {
    USD: "US dollar", AUD: "Australian dollar", BGN: "Bulgarian lev", BRL: "Brazilian real", CAD: "Canadian dollar", CHF: "Swiss franc", CNY: "Chinese yuan", CZK: "Czech koruna", DKK: "Danish krone", EUR: "Euro", GBP: "British pound", HKD: "Hong Kong dollar", HUF: "Hungarian forint", IDR: "Indonesian rupiah", ILS: "Israeli new shekel", INR: "Indian rupee", ISK: "Icelandic króna", JPY: "Japanese yen", KRW: "South Korean won", MXN: "Mexican peso", MYR: "Malaysian ringgit", NOK: "Norwegian krone", NZD: "New Zealand dollar", PHP: "Philippine peso", PLN: "Polish złoty", RON: "Romanian leu", SEK: "Swedish krona", SGD: "Singapore dollar", THB: "Thai baht", TRY: "Turkish lira", ZAR: "South African rand",
};
/** Currencies written without decimals. */
const WHOLE = new Set(["JPY", "KRW", "HUF", "ISK", "IDR"]);
/** What an administrator sets: the currency and where its rate comes from. */
export const organisationCurrencySchema = z
    .object({
    code: currencyCodeSchema,
    /** `daily`: the European Central Bank's reference rate, refreshed each day. `fixed`: `perUsd`, set by hand. */
    rate: z.enum(["daily", "fixed"]).default("daily"),
    /** Units of the currency one US dollar buys; required when `rate` is `fixed`. */
    perUsd: z.number().positive().max(1_000_000).optional(),
    /** When an administrator set it (the platform fills this in). */
    setAt: z.string().optional(),
})
    .refine((c) => c.rate !== "fixed" || c.perUsd !== undefined, { message: "a fixed rate needs perUsd" });
export const USD_MONEY = { currency: "USD", perUsd: 1, source: "usd" };
/** The currency amounts are shown in: the organisation's, or US dollars while it has no rate. */
export function shownCurrency(m) {
    return m.source === "none" ? "USD" : m.currency;
}
/** A US-dollar amount in the organisation's currency (unrounded). */
export function toDisplay(usd, m) {
    return m.source === "none" ? usd : usd * m.perUsd;
}
/** A display-currency amount back to US dollars, for budgets typed in the organisation's currency. */
export function toUsd(amount, m) {
    return m.source === "none" ? amount : amount / m.perUsd;
}
/** The currency's symbol as English readers expect it, unambiguous for every dollar: US$, A$, NZ$, CA$, HK$, S$, €, £, ¥. */
export function currencySymbol(code) {
    if (code === "USD")
        return "US$";
    if (code === "SGD")
        return "S$";
    const parts = new Intl.NumberFormat("en", { style: "currency", currency: code, currencyDisplay: "symbol" }).formatToParts(0);
    const sym = parts.find((p) => p.type === "currency")?.value ?? code;
    return sym === "$" ? `${code.slice(0, 2)}$` : sym;
}
/**
 * Formats a US-dollar amount in the organisation's currency: `A$12.34`, `€0.0123` with `digits`.
 * Whole-unit currencies never show decimals unless `digits` asks for more than two.
 */
export function formatMoney(usd, m, opts = {}) {
    const code = shownCurrency(m);
    const value = toDisplay(usd, m);
    const digits = opts.digits ?? (opts.compact && Math.abs(value) >= 100 ? 0 : 2);
    const shown = WHOLE.has(code) && digits <= 2 ? 0 : digits;
    const sign = value < 0 ? "-" : "";
    return `${sign}${currencySymbol(code)}${Math.abs(value).toLocaleString("en", { minimumFractionDigits: shown, maximumFractionDigits: shown })}`;
}
//# sourceMappingURL=money.js.map